import React, {useState, useEffect, useCallback, useRef} from "react";
import {Button, Popconfirm, Row, Table, Tag, Tooltip, Input, Modal, message, Popover} from "antd";
import {HistoryOutlined, InfoCircleOutlined, LeftOutlined, RightOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../../../hooks/useStore";
import {useFetch} from "../../../../../hooks/useFetch";
import {DocumentHistoryModal} from "../documentHistoryModal/DocumentHistoryModal";
import CourierDocumentsDownload from "./CourierDocumentsDownload";
import {SendToAccountingButton} from "../sendToAccountingButton/SendToAccountingButton";
import JSONInput from "react-json-editor-ajrm";
import {
    ResponsiveTableContainer,
    HistoryButtonWrapper,
    HistoryRejectionBadge
} from "./style/CourierDocuments.styled";
import {RiSparklingFill} from "react-icons/ri";

const MIN_REJECT_REASON_LENGTH = 15;
const AUTO_VALIDATION_STATUS_MAP = {
    approved: "Onaylandı",
    controlled: "Onaylandı",
    pending: "İnceleniyor",
    pendingManualApproval: "Manuel Onay Bekliyor",
    invalid: "Reddedildi"
};

export const CourierDocuments = ({courier, reloadCourier, loading}) => {
    const {documents} = courier;
    const stores = useStore();
    const {t} = useTranslation(["courierDetail"]);
    const documentNameMap = {
        drivingLicenseFront: "Ehliyet Ön Yüz",
        drivingLicenseBack: "Ehliyet Arka Yüz",
        criminalRecord: "Adli sicil kaydı",
        personCompanyTaxCertificate: "Şahıs şirketi vergi levhası",
        businessStartNoticeDoc: "İşe Başlama Bildirim Belgesi",
        penaltyPointsDocument: "Sürücü Belgesi ve Şahıslara Yazılan Ceza Puanı Belgesi",
        residenceCertificate: "İkametgah belgesi",
        identityCardFront: "Kimlik ön yüz",
        identityCardBack: "Kimlik arka yüz",
        vehicleLicense: "Araç ruhsatı",
        srcCertificate: "SRC belgesi",
        psychotechnicalCertificate: "Psikoteknik belgesi",
        profilePicture: "Profil Resmi",
        p1Certificate: "P1* Belgesi"
    };

    const robotControlFieldMap = {
        tckn: "TCKN",
        fullname: "Ad Soyad",
        birthDate: "Doğum Tarihi",
        seriNo: "Seri No",
        sonGecerlilikTarihi: "Son Geçerlilik Tarihi",
        uyrugu: "Uyruk",
        imza: "İmza",
        confidence: "Güven",
        plate: "Plaka",
        brand: "Marka",
        year: "Yıl",
        licenseTypeMatch: "Lisans Tipi Eşleşme",
        validDateRange: "Geçerlilik Süresi",
        bloodGroup: "Kan Grubu",
        licenseCategoryCoverage: "Lisans Kategori Örtüşmesi",
        issuer: "Veren Makam",
        validDates: "Geçerlilik Tarihleri",
        address: "Adres",
        barcode: "Barkod",
        missingArchivedRecord: "Arşiv Bilgileri Eksik mi?",
        hasCriminalRecord: "Adli Sicil Kaydı Var mı?",
        recordDetailsEmpty: "Adli Sicil Kaydı Detayları Boş mu?",
        drivingLicenseExpiry: "Ehliyetin Geçerlilik Süresi",
        drivingLicenseClass: "Ehliyetin Sınıfı",
        eDevlet: "E-Devlet Belge Doğrulama",
        isDocumentValid: "Belge Geçerli Mi?",
        naceKodu: "Faaliyet Kodu (NACE)"
    };

    const [documentHistoryModal, setDocumentHistoryModal] = useState(false);
    const [documentHistoryModalDocumentType, setDocumentHistoryModalDocumentType] = useState(null);
    const [documentViewerModal, setDocumentViewerModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(0);
    const [documentList, setDocumentList] = useState([]);
    const [documentRejectReason, setDocumentRejectReason] = useState("");
    const [quickRejectModal, setQuickRejectModal] = useState(false);
    const [quickRejectDocument, setQuickRejectDocument] = useState(null);
    const [openingDocument, setOpeningDocument] = useState(null);
    const modalOpenTimeoutRef = useRef(null);

    const getDocumentName = key => documentNameMap[key] || key;

    // Platform tespiti
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    const approveDocument = useCallback(
        async key => {
            const result = await stores.courierStore.updateCourierDocumentStatus({
                courierId: courier._id,
                documentType: key,
                status: "approved"
            });
            if (result) {
                reloadCourier();
            }
        },
        [courier._id, reloadCourier, stores.courierStore]
    );
    const rejectDocument = async (key, documentRejectReason) => {
        if (!documentRejectReason) {
            message.error("Lütfen reddetme sebebini giriniz.");
            return;
        }
        if (documentRejectReason?.trim()?.length < MIN_REJECT_REASON_LENGTH) {
            message.error(
                `Lütfen reddetme sebebini en az ${MIN_REJECT_REASON_LENGTH} karakter olacak şekilde giriniz.`
            );
            return;
        }
        try {
            const result = await stores.courierStore.updateCourierDocumentStatus({
                courierId: courier._id,
                documentType: key,
                status: "rejected",
                reason: documentRejectReason
            });
            if (result) {
                reloadCourier();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setDocumentRejectReason("");
        }
    };

    const openDocumentHistoryModal = documentType => {
        setDocumentHistoryModalDocumentType(documentType);
        setDocumentHistoryModal(true);
    };
    const closeDocumentHistoryModal = () => {
        setDocumentHistoryModalDocumentType(null);
        setDocumentHistoryModal(false);
    };

    const openQuickRejectModal = useCallback(document => {
        setQuickRejectDocument(document);
        setQuickRejectModal(true);
        setDocumentRejectReason("");
    }, []);

    const closeQuickRejectModal = () => {
        setQuickRejectDocument(null);
        setQuickRejectModal(false);
        setDocumentRejectReason("");
    };

    const handleQuickReject = async () => {
        if (quickRejectDocument) {
            await rejectDocument(quickRejectDocument.key, documentRejectReason);
            closeQuickRejectModal();
        }
    };
    const checkPermission = useCallback(
        ({key, documentType}) => {
            const activationStep = courier?.activationStep;
            const activationSubStep = courier?.activationSubStep;
            const activationPhase = courier?.activationPhase;

            const permissions = {
                approveDocuments:
                    stores.meStore.checkRole("courierDocumentsApprove") &&
                    ((activationStep === "candidate" &&
                        activationSubStep === "documentsInReview") ||
                        ["p1Certificate"].includes(documentType)) &&
                    activationStep !== "rejected",
                rejectDocuments:
                    ((activationStep === "candidate" &&
                        activationSubStep === "documentsInReview") ||
                        (activationStep === "candidate" && activationPhase === "project") ||
                        (activationStep === "candidate" && activationPhase === "training") ||
                        ["p1Certificate"].includes(documentType)) &&
                    activationStep !== "rejected",
                showApprovalButtons: stores.meStore.checkRole("courierDocumentsApprove"),
                viewCourierDocumentsRejectionHistory: stores.meStore.checkRole(
                    "viewCourierDocumentsRejectionHistory"
                ),
                downloadCourierDocuments: stores.meStore.checkRole("downloadCourierDocuments")
            };
            // Alt kullanıcı (Acente Temsilcisi) yetkisi: görüntüleme/indirme ve onay/ret tek bir
            // "Kurye Belge İşlemleri" yetkisinde birleştirildi (bkz. constants/pagePermissions.js).
            return (
                (permissions?.[key] &&
                    stores.meStore.checkPermission("couriers.documentActions")) ||
                false
            );
        },
        [courier?.activationStep, courier?.activationSubStep, stores.meStore]
    );

    const [, rejectionCountsResult] = useFetch({
        defaultDataValue: {rejectionCounts: {}, historyCounts: {}},
        fetchFn: stores.courierStore.getCourierDocumentRejectionCounts.bind(null, {
            courierId: courier._id
        }),
        dependencies: [courier._id, loading],
        condition:
            Boolean(courier._id) &&
            !loading &&
            stores.meStore.checkRole("viewCourierDocumentsRejectionHistory")
    });

    const documentRejectionCounts = rejectionCountsResult?.rejectionCounts || {};
    const documentHistoryCounts = rejectionCountsResult?.historyCounts || {};

    // Belge listesini hazırla
    const prepareDocumentList = () => {
        return Object.entries(documents)
            .map(([key, value]) => ({
                key: key,
                name: value?.name,
                publicUrl: value?.file?.[0]?.publicUrl,
                status: value?.status,
                mimetype: value?.mimetype || value?.file?.[0]?.mimetype,
                processedByAutomation: Boolean(value?.processedByAutomation)
            }))
            .filter(doc => doc.publicUrl) // Sadece URL'i olan belgeleri al
            .sort((a, b) => getDocumentName(a.key).localeCompare(getDocumentName(b.key)));
    };

    // Dosyanın görsel dosya olup olmadığını kontrol et
    const isImageFile = mimetype => {
        return mimetype && mimetype.startsWith("image/");
    };

    const openDocumentViewerModal = document => {
        // Aynı belge zaten açılıyor veya açık durumdaysa işlem yapma
        if (
            openingDocument === document.key ||
            (documentViewerModal && selectedDocument?.key === document.key)
        ) {
            return;
        }

        // Belge açılmaya başladığını işaretle
        setOpeningDocument(document.key);

        // Önceki timeout'ı temizle
        if (modalOpenTimeoutRef.current) {
            clearTimeout(modalOpenTimeoutRef.current);
        }

        // Modal zaten açıksa önce kapat ve state'i temizle
        if (documentViewerModal) {
            closeDocumentViewerModal();
            // Modal kapatma işleminden sonra kısa bir bekleme süresi ekle
            setTimeout(() => {
                openDocumentViewerModal(document);
            }, 200);
            return;
        }

        // State güncellemelerini batch olarak yap
        modalOpenTimeoutRef.current = setTimeout(() => {
            try {
                const docs = prepareDocumentList();
                const index = docs.findIndex(doc => doc.key === document.key);

                // Tüm state'leri tek seferde güncelle
                setDocumentList(docs);
                setSelectedDocumentIndex(index);
                setSelectedDocument(document);
                setDocumentViewerModal(true);

                // Modal açıldığında opening state'ini temizle
                setOpeningDocument(null);
            } catch (error) {
                console.error("Belge açılırken hata oluştu:", error);
                // Hata durumunda opening state'ini temizle
                setOpeningDocument(null);
            } finally {
                // Timeout tamamlandığında referansı temizle
                modalOpenTimeoutRef.current = null;
            }
        }, 100); // Kısa bir gecikme ile state temizlenmesini garanti et
    };

    const closeDocumentViewerModal = () => {
        // Bekleyen timeout'ları temizle
        if (modalOpenTimeoutRef.current) {
            clearTimeout(modalOpenTimeoutRef.current);
            modalOpenTimeoutRef.current = null;
        }

        setSelectedDocument(null);
        setDocumentViewerModal(false);
        setDocumentList([]);
        setSelectedDocumentIndex(0);
        setOpeningDocument(null); // Açılmakta olan belge state'ini temizle
    };

    // Sonraki belgeye geç
    const goToNextDocument = () => {
        if (selectedDocumentIndex < documentList.length - 1) {
            const nextIndex = selectedDocumentIndex + 1;
            setSelectedDocumentIndex(nextIndex);
            setSelectedDocument(documentList[nextIndex]);
        }
    };

    // Önceki belgeye geç
    const goToPreviousDocument = () => {
        if (selectedDocumentIndex > 0) {
            const prevIndex = selectedDocumentIndex - 1;
            setSelectedDocumentIndex(prevIndex);
            setSelectedDocument(documentList[prevIndex]);
        }
    };

    // Klavye desteği için event listener
    useEffect(() => {
        const handleKeyPress = event => {
            if (!documentViewerModal) return;

            // Navigasyon kısayolları (sadece birden fazla belge varsa)
            if (documentList.length > 1) {
                if (event.key === "ArrowRight") {
                    event.preventDefault();
                    goToNextDocument();
                } else if (event.key === "ArrowLeft") {
                    event.preventDefault();
                    goToPreviousDocument();
                }
            }

            // Onay/Red kısayolları
            if (selectedDocument && selectedDocument.publicUrl) {
                const isApproveShortcut = isMac
                    ? event.altKey && (event.key === "o" || event.key === "ø" || event.key === "ö")
                    : event.ctrlKey && event.shiftKey && event.key === "O";

                const isRejectShortcut = isMac
                    ? event.altKey && (event.key === "r" || event.key === "®")
                    : event.ctrlKey && event.shiftKey && event.key === "R";

                const isDocReview =
                    courier?.activationStep === "candidate" &&
                    courier?.activationSubStep === "documentsInReview";

                if (isApproveShortcut) {
                    event.preventDefault();
                    // Onay işlemi - documentsInReview'da onaylıysa engelle, reddedilmişse izin ver
                    const approveAllowed =
                        selectedDocument?.name &&
                        !(isDocReview && selectedDocument?.status === "approved") &&
                        checkPermission({
                            key: "approveDocuments",
                            documentType: selectedDocument.key
                        });
                    if (approveAllowed) {
                        approveDocument(selectedDocument.key);
                    } else {
                        message.warning(
                            "Bu belge onaylanamaz. Belge durumu veya yetki kontrolü yapın."
                        );
                    }
                } else if (isRejectShortcut) {
                    event.preventDefault();
                    // Red işlemi - documentsInReview'da reddedilmişse engelle, onaylıysa izin ver
                    const rejectAllowed =
                        selectedDocument?.name &&
                        !(isDocReview && selectedDocument?.status === "rejected") &&
                        checkPermission({
                            key: "rejectDocuments",
                            documentType: selectedDocument.key
                        });
                    if (rejectAllowed) {
                        // Red modal'ını aç
                        openQuickRejectModal(selectedDocument);
                    } else {
                        message.warning(
                            "Bu belge reddedilemez. Belge durumu veya yetki kontrolü yapın."
                        );
                    }
                }
            }
        };

        if (documentViewerModal) {
            document.addEventListener("keydown", handleKeyPress);
        }

        return () => {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [
        documentViewerModal,
        selectedDocumentIndex,
        documentList,
        selectedDocument,
        isMac,
        approveDocument,
        openQuickRejectModal,
        checkPermission
    ]);

    // Courier documents değiştiğinde modal state'ini güncelle
    useEffect(() => {
        if (documentViewerModal && selectedDocument) {
            const updatedDocumentList = prepareDocumentList();
            const updatedSelectedDocument = updatedDocumentList.find(
                doc => doc.key === selectedDocument.key
            );

            if (updatedSelectedDocument) {
                setDocumentList(updatedDocumentList);
                setSelectedDocument(updatedSelectedDocument);
                // Index'i de güncelle
                const newIndex = updatedDocumentList.findIndex(
                    doc => doc.key === selectedDocument.key
                );
                if (newIndex !== -1) {
                    setSelectedDocumentIndex(newIndex);
                }
            }
        }
    }, [courier.documents, documentViewerModal, selectedDocument?.key]);

    // Component unmount olduğunda timeout'ları temizle
    useEffect(() => {
        return () => {
            if (modalOpenTimeoutRef.current) {
                clearTimeout(modalOpenTimeoutRef.current);
            }
            setOpeningDocument(null); // Açılmakta olan belge state'ini temizle
        };
    }, []);

    const documentStatusColorMap = {
        waitingApprove: "orange",
        approved: "green",
        rejected: "red"
    };
    const defaultDocumentStatusColor = "#919190";
    const autoValidationColorMap = {
        approved: "green",
        controlled: "green",
        pending: "blue",
        pendingManualApproval: "orange",
        invalid: "red"
    };
    const getAutoValidationColor = status => autoValidationColorMap[status] || "#919190";
    const getDocumentStatusColor = ({document}) =>
        Boolean(document?.publicUrl)
            ? documentStatusColorMap?.[document?.status] || defaultDocumentStatusColor
            : defaultDocumentStatusColor;

    const documentStatusTextMap = {
        waitingApprove: "Onay Bekliyor",
        approved: "Onaylandı",
        rejected: "Reddedildi"
    };
    const getDocumentStatusText = ({document}) =>
        Boolean(document?.publicUrl)
            ? documentStatusTextMap?.[document?.status] || document?.status
            : "Belge Yüklenmemiş";

    const getControlFields = useCallback(({controls = []}) => {
        const controlResponse = {};
        controls?.forEach(control => {
            controlResponse[
                `${robotControlFieldMap?.[control?.key] || control?.key} `
            ] = control?.isValid ? "Onaylandı" : "Reddedildi";
        });
        return controlResponse;
    }, []);

    const renderActionButtons = (document, inModalView = false) => {
        const renderDocumentStatusTag = () => (
            <Tooltip
                title={
                    ["approved", "rejected"].includes(document?.status) &&
                    document?.processedByAutomation
                        ? `Bu belge otomatik olarak ${
                              document?.status === "approved"
                                  ? "onaylanmıştır."
                                  : document?.status === "rejected"
                                  ? "reddedilmiştir."
                                  : "değerlendirilmiştir."
                          }`
                        : getDocumentStatusText({document})
                }
            >
                <Tag
                    key={document.status}
                    color={getDocumentStatusColor({document})}
                    style={{display: "flex", alignItems: "center", gap: "5px"}}
                >
                    {getDocumentStatusText({document})}
                    {document?.processedByAutomation ? (
                        <>
                            <RiSparklingFill />
                        </>
                    ) : (
                        <></>
                    )}
                </Tag>
            </Tooltip>
        );
        const renderApproveButton = ({document}) => {
            const isDocReview =
                courier?.activationStep === "candidate" &&
                courier?.activationSubStep === "documentsInReview";
            const isProjectOrTraining =
                courier?.activationStep === "candidate" &&
                (courier?.activationPhase === "project" || courier?.activationPhase === "training");

            // Project/training adımlarında onayla butonu gösterilmez
            if (isProjectOrTraining) {
                return null;
            }

            // Belge inceleme adımında: onaylıysa buton deaktif, reddedilmişse aktif
            const isDisabled =
                !document?.name ||
                (isDocReview && document?.status === "approved") ||
                !checkPermission({
                    key: "approveDocuments",
                    documentType: document.key
                });

            return (
                <Tooltip title={`Belgeyi Onayla`} placement="bottom">
                    <Button
                        loading={loading}
                        type="primary"
                        size="small"
                        style={{
                            backgroundColor: "green",
                            borderColor: "green",
                            color: "white",
                            opacity: isDisabled ? 0.5 : 1
                        }}
                        disabled={isDisabled}
                        onClick={() => approveDocument(document.key)}
                    >
                        Onayla
                    </Button>
                </Tooltip>
            );
        };

        const renderRejectButton = ({document, courier}) => {
            const isDocReview =
                courier?.activationStep === "candidate" &&
                courier?.activationSubStep === "documentsInReview";
            const isProjectOrTraining =
                (courier?.activationStep === "candidate" &&
                    courier?.activationPhase === "project") ||
                (courier?.activationStep === "candidate" &&
                    courier?.activationPhase === "training");

            // Belge inceleme adımında: reddedilmişse deaktif, onaylıysa veya beklemedeyse aktif
            // Project/training adımlarında: her zaman aktif
            const isDisabled =
                !document?.name ||
                (isDocReview && document?.status === "rejected") ||
                !checkPermission({
                    key: "rejectDocuments",
                    documentType: document.key
                });

            return (
                <Tooltip title={`Belgeyi Reddet`} placement="bottom">
                    <Popconfirm
                        placement="topRight"
                        icon={<></>}
                        title={
                            <>
                                <p>Belgeyi reddetmek istediğinize emin misiniz? </p>
                                <p>
                                    Kurye adayına geri bildirim vermek için lütfen belgeyi neden
                                    reddettiğinizi belirtin.
                                </p>
                                <Input.TextArea
                                    placeholder="Kuryeye sms ve mail olarak gönderilecek olan red nedenini giriniz."
                                    style={{marginTop: "10px"}}
                                    value={documentRejectReason}
                                    onChange={e => {
                                        setDocumentRejectReason(e.target.value);
                                    }}
                                    rows={4}
                                />
                            </>
                        }
                        overlayStyle={{
                            width: "300px"
                        }}
                        onVisibleChange={() => setDocumentRejectReason("")}
                        onConfirm={() => rejectDocument(document.key, documentRejectReason)}
                        okText={"Reddet"}
                        cancelText={"İptal"}
                        okButtonProps={{disabled: !documentRejectReason}}
                        disabled={isDisabled}
                    >
                        <Button
                            loading={loading}
                            type="primary"
                            size="small"
                            style={{
                                marginLeft: "5px",
                                backgroundColor: "red",
                                borderColor: "red",
                                color: "white",
                                opacity: isDisabled ? 0.5 : 1
                            }}
                            onClick={() => setDocumentRejectReason("")}
                            disabled={isDisabled}
                        >
                            Reddet
                        </Button>
                    </Popconfirm>
                </Tooltip>
            );
        };

        const renderHistoryButton = () => {
            const rejectionCount = documentRejectionCounts[document?.key] || 0;
            const historyCount = documentHistoryCounts[document?.key] || 0;
            const hasHistory = historyCount > 0 || rejectionCount > 0;
            const tooltipTitle = !hasHistory
                ? t("documentHistory.emptyText")
                : rejectionCount > 0
                ? t("documentHistory.rejectionCountTooltip", {count: rejectionCount})
                : t("documentHistory.tooltip");

            return (
                <HistoryButtonWrapper>
                    <Tooltip title={tooltipTitle}>
                        <span>
                            <HistoryRejectionBadge
                                count={rejectionCount}
                                showZero={false}
                                offset={[-2, 2]}
                                style={{backgroundColor: "#ff4d4f"}}
                            >
                                <Button
                                    size="small"
                                    icon={<HistoryOutlined />}
                                    disabled={!hasHistory}
                                    onClick={() => openDocumentHistoryModal(document?.key)}
                                />
                            </HistoryRejectionBadge>
                        </span>
                    </Tooltip>
                </HistoryButtonWrapper>
            );
        };
        return (
            <Row justify="center">
                {inModalView && ["approved", "rejected"].includes(document?.status) ? (
                    renderDocumentStatusTag()
                ) : (
                    <></>
                )}
                {checkPermission({key: "showApprovalButtons"}) ? (
                    <>
                        {renderApproveButton({document})}
                        {renderRejectButton({document, courier})}
                    </>
                ) : null}
                {checkPermission({key: "viewCourierDocumentsRejectionHistory"}) &&
                    renderHistoryButton()}
            </Row>
        );
    };

    const visibleColumns = [
        {
            title: "Belge Türü",
            dataIndex: "key",
            key: "key",
            render: (key, document) => (
                <Row align="middle" style={{gap: "8px"}}>
                    <span>{getDocumentName(key)}</span>

                    {document?.name ? (
                        <Tooltip title={document?.name}>
                            <InfoCircleOutlined style={{marginLeft: "5px"}} />
                        </Tooltip>
                    ) : null}

                    {key === "personCompanyTaxCertificate" && (
                        <SendToAccountingButton
                            courier={courier}
                            reloadCourier={reloadCourier}
                            size="small"
                            type="default"
                        />
                    )}
                </Row>
            ),
            sorter: (a, b) => getDocumentName(a.key).localeCompare(getDocumentName(b.key)),
            defaultSortOrder: "ascend"
        },
        {
            title: "Durum",
            dataIndex: "status",
            key: "status",
            render: (status, document) => (
                <Tooltip
                    title={
                        ["approved", "rejected"].includes(document?.status) &&
                        document?.processedByAutomation
                            ? `Bu belge otomatik olarak ${
                                  document?.status === "approved"
                                      ? "onaylanmıştır."
                                      : "reddedilmiştir."
                              }`
                            : null
                    }
                >
                    <Tag
                        color={getDocumentStatusColor({document})}
                        key={status}
                        style={{display: "inline-flex", alignItems: "center", gap: "5px"}}
                    >
                        {getDocumentStatusText({document})}
                        {document?.processedByAutomation ? <RiSparklingFill /> : null}
                    </Tag>
                </Tooltip>
            )
        },
        {
            title: "Link",
            dataIndex: "publicUrl",
            key: "publicUrl",
            render: (url, document) =>
                url ? (
                    <Button
                        type="primary"
                        onClick={() => openDocumentViewerModal(document)}
                        size="small"
                        loading={openingDocument === document.key}
                        disabled={openingDocument === document.key || documentViewerModal}
                    >
                        {openingDocument === document.key
                            ? "Açılıyor..."
                            : documentViewerModal
                            ? "Görüntüle"
                            : "Görüntüle"}
                    </Button>
                ) : (
                    "-"
                ),
            hidden: !checkPermission({key: "downloadCourierDocuments"})
        },

        {
            title: "Onayla / Reddet",
            align: "center",
            render: (_, document) => renderActionButtons(document),
            hidden:
                !checkPermission({key: "showApprovalButtons"}) ||
                !checkPermission({key: "viewCourierDocumentsRejectionHistory"})
        },

        {
            title: "Otomatik Onay Durumu",
            dataIndex: "autoValidation",
            key: "autoValidation",
            render: autoValidation => {
                if (typeof autoValidation?.status === "string") {
                    return (
                        <>
                            <Tag
                                color={getAutoValidationColor(autoValidation?.status)}
                                key={autoValidation?.status}
                            >
                                {AUTO_VALIDATION_STATUS_MAP[autoValidation?.status] || "Bilinmiyor"}
                            </Tag>
                            <Popover
                                content={
                                    <JSONInput
                                        id="controlFieldsJSON"
                                        placeholder={getControlFields({
                                            controls: autoValidation?.controls
                                        })}
                                        theme="dark_mitsuketa_tribute"
                                        width="500px"
                                        height="auto"
                                        viewOnly={true}
                                        confirmGood={false}
                                        style={{keyStyle: "doubleQuotes"}}
                                    />
                                }
                            >
                                <InfoCircleOutlined style={{paddingLeft: "5px"}} />
                            </Popover>
                        </>
                    );
                }
                return "-";
            }
        }
    ].filter(column => column?.hidden !== true);

    const tableData = Object.entries(documents)
        .map(([key, value]) => ({
            key: key,
            name: value?.name,
            publicUrl: value?.file?.[0]?.publicUrl,
            status: value?.status,
            mimetype: value?.mimetype || value?.file?.[0]?.mimetype,
            autoValidation: value?.autoValidation,
            processedByAutomation: Boolean(value?.processedByAutomation)
        }))
        .sort((a, b) => getDocumentName(a.key).localeCompare(getDocumentName(b.key)));

    return (
        <>
            <ResponsiveTableContainer
                loading={loading}
                pagination={false}
                scroll={{x: "max-content"}}
                columns={visibleColumns.map(column => ({
                    ...column,
                    onCell: () => ({
                        "data-label": column.title
                    })
                }))}
                dataSource={tableData}
            />
            <DocumentHistoryModal
                documentName={
                    documentHistoryModalDocumentType
                        ? getDocumentName(documentHistoryModalDocumentType)
                        : ""
                }
                visible={documentHistoryModal}
                onClose={closeDocumentHistoryModal}
                courierId={courier._id}
                documentType={documentHistoryModalDocumentType}
            />
            <Modal
                title={
                    selectedDocument
                        ? `${getDocumentName(selectedDocument.key)} ${
                              documentList.length > 1
                                  ? `(${selectedDocumentIndex + 1}/${documentList.length})`
                                  : ""
                          }`
                        : "Belge Görüntüleyici"
                }
                visible={documentViewerModal}
                onCancel={closeDocumentViewerModal}
                footer={
                    selectedDocument ? (
                        <div>
                            <Row justify="space-between" align="middle">
                                <CourierDocumentsDownload
                                    courierId={courier._id}
                                    documentType={selectedDocument.key}
                                />
                                {renderActionButtons(selectedDocument, true)}
                            </Row>
                            {/* Klavye kısayolları bilgilendirmesi */}
                            <Row
                                justify="center"
                                style={{marginTop: 8, fontSize: 12, color: "#666"}}
                            >
                                <span>
                                    Klavye kısayolları: {isMac ? "Option+O" : "Ctrl+Shift+O"}{" "}
                                    (Onayla) | {isMac ? "Option+R" : "Ctrl+Shift+R"} (Reddet)
                                    {documentList.length > 1 && " | ← → (Navigasyon)"}
                                </span>
                            </Row>
                        </div>
                    ) : null
                }
                width={800}
                style={{top: 50, position: "relative"}}
                bodyStyle={{
                    padding: 0,
                    height: "600px"
                }}
            >
                {isImageFile(selectedDocument?.mimetype) ? (
                    <img
                        src={selectedDocument?.publicUrl}
                        alt={getDocumentName(selectedDocument.key)}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain"
                        }}
                    />
                ) : (
                    <iframe
                        src={selectedDocument?.publicUrl}
                        style={{
                            width: "100%",
                            height: "100%",
                            border: "none"
                        }}
                    />
                )}

                {/* Belge Navigasyon Ok Butonları */}
                {documentList.length > 1 && (
                    <>
                        {/* Sol Ok - Önceki Belge */}
                        <Button
                            type="primary"
                            shape="circle"
                            size="large"
                            icon={<LeftOutlined />}
                            onClick={goToPreviousDocument}
                            disabled={selectedDocumentIndex === 0}
                            style={{
                                position: "absolute",
                                left: "-66px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 1001,
                                width: "50px",
                                height: "50px",
                                fontSize: "18px",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
                            }}
                        />

                        {/* Sağ Ok - Sonraki Belge */}
                        <Button
                            type="primary"
                            shape="circle"
                            size="large"
                            icon={<RightOutlined />}
                            onClick={goToNextDocument}
                            disabled={selectedDocumentIndex === documentList.length - 1}
                            style={{
                                position: "absolute",
                                right: "-66px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 1001,
                                width: "50px",
                                height: "50px",
                                fontSize: "18px",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
                            }}
                        />
                    </>
                )}
            </Modal>
            {/* Hızlı Red Modal */}
            <Modal
                title={`Belgeyi Reddet: ${
                    quickRejectDocument ? getDocumentName(quickRejectDocument.key) : ""
                }`}
                visible={quickRejectModal}
                onCancel={closeQuickRejectModal}
                onOk={handleQuickReject}
                okText="Reddet"
                cancelText="İptal"
                okButtonProps={{
                    disabled:
                        !documentRejectReason ||
                        documentRejectReason?.trim()?.length < MIN_REJECT_REASON_LENGTH,
                    danger: true
                }}
                width={500}
            >
                <p>Bu belgeyi reddetmek istediğinize emin misiniz?</p>
                <p>
                    Kurye adayına geri bildirim vermek için lütfen belgeyi neden reddettiğinizi
                    belirtin.
                </p>
                <Input.TextArea
                    placeholder="Kuryeye SMS ve e-posta olarak gönderilecek olan red nedenini giriniz."
                    value={documentRejectReason}
                    onChange={e => setDocumentRejectReason(e.target.value)}
                    rows={4}
                    maxLength={500}
                    showCount
                    autoFocus
                />
                {documentRejectReason?.trim()?.length < MIN_REJECT_REASON_LENGTH && (
                    <div style={{color: "red", fontSize: "12px", marginTop: "5px"}}>
                        En az {MIN_REJECT_REASON_LENGTH} karakter girmelisiniz.
                    </div>
                )}
            </Modal>
        </>
    );
};
